/**
 * Lightweight Google Tag Manager dataLayer helper.
 *
 * Pushes structured events to `window.dataLayer` so GTM can route them to
 * GA4, AdSense conversions, ad audiences, etc. without coupling our code
 * to any specific tag.
 *
 * Usage:
 *   import { trackEvent } from '@/lib/analytics';
 *   trackEvent('phrase_save', { phrase_id: 42, phrase_text: 'hello' });
 *
 * Conventions (match GA4 recommended event naming where applicable):
 *   sign_up, login, phrase_save, phrase_unsave, phrase_view,
 *   level_up, streak_milestone, badge_earned,
 *   interpreter_rephrase, rephrase_saved,
 *   subscription_started, blog_view, blog_share, helpbot_question
 */

export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined') return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...params,
      // Useful global context every event will carry:
      app_section: window.location.pathname.split('/')[1] || 'home',
      ts: new Date().toISOString(),
    });
  } catch (e) {
    // Never let analytics break the app.
    // eslint-disable-next-line no-console
    console.warn('[analytics] failed to push event', eventName, e);
  }
}

/** Convenience wrappers for the most-fired events. */
export const track = {
  phraseSave: (phraseId, phraseText) =>
    trackEvent('phrase_save', { phrase_id: phraseId, phrase_text: phraseText }),
  phraseUnsave: (phraseId) =>
    trackEvent('phrase_unsave', { phrase_id: phraseId }),
  levelUp: (level) =>
    trackEvent('level_up', { level }),
  streakMilestone: (streakDay) =>
    trackEvent('streak_milestone', { streak_day: streakDay }),
  badgeEarned: (badgeId, badgeName) =>
    trackEvent('badge_earned', { badge_id: badgeId, badge_name: badgeName }),
  signUp: (method) =>
    trackEvent('sign_up', { method }),
  login: (method) =>
    trackEvent('login', { method }),
  interpreterRephrase: (tone, language) =>
    trackEvent('interpreter_rephrase', { tone, language }),
  rephraseSaved: () =>
    trackEvent('rephrase_saved', {}),
  subscriptionStarted: (plan, valueUsd) =>
    trackEvent('subscription_started', {
      plan,
      value: valueUsd,
      currency: 'USD',
    }),
  blogView: (slug, category) =>
    trackEvent('blog_view', { slug, category }),
  blogShare: (slug, channel) =>
    trackEvent('blog_share', { slug, channel }),
  helpbotQuestion: () =>
    trackEvent('helpbot_question', {}),
};

export default trackEvent;
